<template lang="pug">
  v-layout(row justify-center)
    v-dialog(
      v-model="reviewDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    )
      v-card
        v-toolbar(dark color="primary")
          v-btn(
            icon
            dark
            @click.native="closeReviewDialog"
          )
            v-icon close
          v-toolbar-title Review optimization
          v-spacer
          v-toolbar-items
            v-btn(
              dark
              flat
              :disabled="activeBarriers.length < 1"
              @click="submitBarriersForCalculation"
            ) Submit
        v-list(three-line subheader)
          v-subheader Placed barriers
        v-toolbar
          v-toolbar-title Actions
          v-divider.mx-2(
            inset
            vertical
          )
          v-spacer
          v-btn(
            :disabled="!anySelectedBarriers"
            :color="barrierDeleteButtonColor"
            @click.primary="deleteSelectedBarriers"
          ) {{ barrierDeleteButtonText }}
        v-data-table(
            :headers="markerTableHeaders"
            :items="markers"
            class="elevation-1"
            v-model="selected"
            select-all
          )
            template(
              slot="items"
              slot-scope="props"
            )
              td
                v-checkbox(
                  v-model="props.selected"
                  primary
                  hide-details
                )
              td {{ props.item.name }}
              td {{ props.item.position.lat.toFixed(4) }}
              td {{ props.item.position.lng.toFixed(4) }}
              td
                v-checkbox(
                  :input-value="barrierActive(props.item.id)"
                  primary
                  hide-details
                  @change="toggleBarrierActive(props.item.id)"
                  )
        v-divider
        v-list(three-line subheader)
          v-subheader Total cost: {{ totalCost }} - Budget: {{ budget }}
        v-data-table(
            :headers="costTableHeaders"
            :items="costPerBarrierType"
            class="elevation-1"
          )
            template(
              slot="items"
              slot-scope="props"
            )
              td {{ props.item.name }}
              td {{ props.item.count }}
              td {{ props.item.cost }}
</template>

<script>
import { mapGetters } from "vuex";
import availableBarrierTypes from "@/utilities/barrierTypes";

import api from "@/store/utils/api";

export default {
  name: "MapReviewDialog",
  data() {
    return {
      markerTableHeaders: [
        {
          text: "Name",
          value: "name"
        },
        {
          text: "Latitute",
          value: "position.lat"
        },
        {
          text: "Longitude",
          value: "position.lng"
        },
        {
          text: "Active?",
          value: "active"
        }
      ],
      costTableHeaders: [
        {
          text: "Name",
          value: "name"
        },
        {
          text: "Count",
          value: "count"
        },
        {
          text: "Cost",
          value: "cost"
        }
      ],
      selected: [],
      currency: "Euro"
    };
  },
  computed: {
    ...mapGetters([
      "reviewDialog",
      "markers",
      "budget",
      "startTime",
      "stopTime",
      "heatmapVisible",
      "getMapBounds"
    ]),
    activeBarriers: function() {
      return this.markers.filter(barrier => barrier.active === true);
    },
    anySelectedBarriers: function() {
      return this.selected.length > 0;
    },
    barrierDeleteButtonColor: function() {
      return this.anySelectedBarriers ? "error" : "primary";
    },
    barrierDeleteButtonText: function() {
      return this.anySelectedBarriers
        ? `Delete (${this.selected.length})`
        : "Delete";
    },
    barrierTypes: () => {
      return availableBarrierTypes;
    },
    totalCost: function() {
      const cost = this.costPerBarrierType.reduce((acc, obj) => {
        return acc + obj.cost;
      }, 0);
      return `${cost} ${this.currency}`;
    },
    uniqueBarrierTypes: function() {
      return [...new Set(this.markers.map(barrier => barrier.type))];
    },
    barrierTypeCount: function() {
      // Snippet found on SO: https://stackoverflow.com/a/37565905
      const count = this.activeBarriers.reduce((prevBarrier, currBarrier) => {
        const name = this.getBarrierNameByType(currBarrier.type).name;
        prevBarrier[name] = (prevBarrier[name] || 0) + 1;
        return prevBarrier;
      }, {});
      return count;
    },
    costPerBarrierType: function() {
      // Snippet found on SO: https://stackoverflow.com/a/37623524
      const costs = this.activeBarriers.reduce((prevBarrier, currBarrier) => {
        let count = prevBarrier.get(currBarrier.type) || 0;
        prevBarrier.set(currBarrier.type, currBarrier.cost + count);
        return prevBarrier;
      }, new Map());

      const costsObject = [...costs].map(([key, value]) => {
        const name = this.getBarrierNameByType(key).name;
        return {
          type: key,
          cost: value,
          name: name,
          count: this.barrierTypeCount[name]
        };
      });

      return costsObject;
    }
  },
  methods: {
    barrierActive: function(id) {
      return this.markers.filter(barrier => barrier.id === id)[0];
    },
    deleteSelectedBarriers: function(id) {
      // Instead of using slice, we filter all values
      const filtered = this.markers.filter(function(e) {
        return this.indexOf(e) < 0;
      }, this.selected);
      this.$store.dispatch("SET_BARRIERS", filtered);
    },
    toggleBarrierActive: function(id) {
      this.$store.dispatch("TOGGLE_BARRIER_ACTIVE_STATE", id);
    },
    getBarrierNameByType: function(type) {
      return availableBarrierTypes.filter(barrier => barrier.type == type)[0];
    },
    getActiveStatusFromID: function(id) {
      return this.markers.filter(barrier => barrier.id === id);
    },
    closeReviewDialog: function() {
      this.$store.dispatch("TOGGLE_REVIEW_DIALOG");
    },
    submitBarriersForCalculation: function() {
      api
        .post("/api/calcDatapoints", {
          startTime: this.startTime,
          stopTime: this.stopTime,
          ...this.getMapBounds,
          barriers: this.activeBarriers,
          budget: this.budget
        })
        .then(response => {
          this.$store
            .dispatch("SET_POINTS", response.data.points)
            .then(() => {
              this.$store.dispatch("TOGGLE_HEATMAP").then(() => {
                // If the heatmap was just disabled, we are going to re-enable it again.
                if (!this.heatmapVisible) {
                  this.$store.dispatch("TOGGLE_HEATMAP");
                }
              });
            });
        });
    }
  }
};</script>
